import axios from "axios";

/**
 * Builds an axios client for ONE auth plane.
 *
 * There is no token here any more. The session lives in an httpOnly cookie that
 * JavaScript cannot read — which is the entire point of the change. This code
 * cannot leak a credential because it never holds one.
 *
 * What that means in practice:
 *   - `withCredentials: true` so the browser attaches the cookie
 *   - no storage, no Authorization header, no refresh logic
 *   - "am I signed in?" is a server question, answered by calling /me
 *
 * Plane separation still holds: each app talks to its own plane, and the server
 * sets a differently-named cookie per plane (gp_member / gp_agent / gp_master).
 *
 * @param {object}   opts
 * @param {string}   opts.plane            "master" | "agent" | "player"
 * @param {string}   opts.baseURL          usually "/api" so Vite proxies it
 * @param {Function} opts.onUnauthorized   called on 401 so the app can redirect
 *
 * Pass `{ expectUnauthorized: true }` in a request config when a 401 is a valid
 * answer rather than a lapsed session — the session-restore call is the one that
 * matters, and without it a public page cannot survive its own first render.
 */
export function createApiClient({ plane, baseURL = "/api", onUnauthorized }) {
  const client = axios.create({
    baseURL,
    timeout: 20000,
    headers: { "Content-Type": "application/json" },

    // Sends the session cookie. Without it the browser withholds cookies on
    // XHR and every authenticated call 401s.
    withCredentials: true,
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;

      // A 401 on the session-restore call is the NORMAL "not signed in" answer,
      // not an expired session. Treating them the same bounced every anonymous
      // visitor off the public lobby to /login?expired=1 on each page load,
      // because restore() runs before the router lets anything render.
      //
      // Callers that expect a 401 opt out by setting expectUnauthorized on the
      // request config.
      if (status === 401 && !error?.config?.expectUnauthorized) {
        // The server already cleared the cookie. Nothing to clean up locally —
        // there is no client-side credential to remove.
        onUnauthorized?.();
      }

      return Promise.reject(normalizeError(error));
    }
  );

  return { client, plane };
}

/**
 * Collapses axios errors into a predictable shape.
 *
 * Deliberately does not surface server detail beyond a message. The API returns
 * one identical message for every auth failure so it cannot be used to enumerate
 * accounts, and the UI must not undo that by being more specific.
 */
function normalizeError(error) {
  const status = error?.response?.status ?? 0;
  const data = error?.response?.data;

  let message = "Something went wrong. Please try again.";

  if (status === 0) message = "Cannot reach the server.";
  else if (status === 401) {
    // The login endpoint always sends its own message, and that one is
    // deliberately identical for every failure so it cannot be used to
    // enumerate accounts. This fallback is therefore the OTHER 401: a session
    // that died mid-use, where the middleware rejects the request with no body.
    // Calling that "Invalid credentials" blames the user for typing something
    // wrong when they typed nothing at all.
    message = data?.message ?? "Your session has ended. Please sign in again.";
  }
  else if (status === 403) {
    // 403 now also covers a rejected cross-origin request, which is a
    // configuration problem rather than a permission one — but the user-facing
    // wording stays the same either way.
    message = data?.message ?? "You do not have access to that.";
  } else if (status === 404) message = "Not found.";
  else if (status === 429) message = "Too many attempts. Please wait a moment.";
  else if (status === 501 || status === 503) {
    // "Not implemented" and "unavailable" are the API saying a feature is not
    // connected yet — a deliberate, player-safe message, not a leaked stack
    // trace. Replacing it with "Server error" turns "this game isn't live yet"
    // into what reads as a broken site.
    message = typeof data?.message === "string" ? data.message : "Not available right now.";
  } else if (status >= 500) message = "Server error. Please try again.";
  else if (typeof data?.message === "string") message = data.message;

  return { status, message, raw: error };
}

/**
 * Money, always to exactly two decimals.
 *
 * Balances are stored as numeric(20,8) — the precision exists for crypto — so an
 * unformatted figure can arrive with eight decimal places and blow a table
 * column apart. maximumFractionDigits pins the display at two regardless; the
 * stored value is untouched, and anything that needs full precision reads it
 * from the API rather than from a rendered string.
 *
 * The fallback is not decoration. Intl requires a three-letter ISO 4217 code and
 * throws RangeError on anything else — "USDT" is four letters and is offered as
 * an account currency, so without this a USDT wallet crashes every screen that
 * renders a balance.
 */
export function formatMoney(value, currency = "MYR") {
  const amount = typeof value === "string" ? Number(value) : value ?? 0;
  const safe = Number.isFinite(amount) ? amount : 0;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe);
  } catch {
    // Not an ISO currency. Show the code beside the number rather than nothing.
    const formatted = safe.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${currency} ${formatted}`;
  }
}

export function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}
